import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Logo, NamedIcon, PartyUiContext, PhaseSwap, Stage, type PartyUi } from '@gmp/shared-ui';
import type { GameId, ScoreLine } from '@gmp/shared-types/browser';
import { party } from '../net/client';
import { inviteUrl } from '../net/config';
import { QR } from '../app/QR';
import { GAME_UI, screenKey } from '../app/games';
import { ART } from '../app/art';
import { FitBox, GameIcon, TvHeader, TvLobbyHeader, TvScoreboard, phaseCaption, phaseKicker, tvTheme } from './TvFrame';
import { gameInfo } from '../app/catalog';
import { sound, usePrefs } from '../app/prefs';
import { usePartyState } from '../App';
import { GrudgeMatch, ResultsBody } from './Results';
import { GameLoading } from './GameShell';
import { FeedTicker } from '../app/Feed';
import { LoadingScreen } from '../app/Loading';
import { useWakeLock } from '../app/cast';
import { TvMusicProbe, useTvMusicDriver } from '../app/tvMusic';
import { SiteFooter } from '../app/Footer';

/**
 * Audience screen for a TV or laptop, opened with just the party code (/display/1234). Receives only the public display
 * view (no objectives, ballots or private assignments), and is laid out to read from across a room.
 */
export function DisplayScreen({ code }: { code: string }) {
  const state = usePartyState();
  const prefs = usePrefs();
  // A TV left on the audience screen should never dim or sleep mid-game.
  useWakeLock(true);
  useEffect(() => {
    if (state.status === 'online' && !state.room) party.watch(code);
  }, [state.status, state.room, code]);

  const ui = useMemo<PartyUi | null>(() => {
    if (!state.room) return null;
    const members = new Map(state.room.members.map((m) => [m.id, m]));
    return {
      now: party.now, me: null, room: state.room, member: (id) => (id ? members.get(id) : undefined),
      act: async () => ({ ok: false, error: 'Display only' }), input: () => undefined, upload: async () => ({ error: 'Display only' }), report: () => undefined,
      display: true, sound, reducedMotion: prefs.reducedMotion || state.room.settings.reducedMotion,
    };
  }, [state.room, prefs.reducedMotion]);

  if (!ui || !state.room)
    return (
      <Stage>
        {state.error ? (
          <div className="flex-1 grid place-items-center text-center p-10">
            <div className="flex flex-col items-center gap-6">
              <Logo size="lg" />
              <p className="text-3xl font-display font-black" data-testid="display-error">{state.error.message}</p>
              <p className="text-xl text-mute max-w-2xl" data-testid="display-error-help">
                {state.error.code === 'room_not_found'
                  ? `No open party has the code ${code}. The code is at the top of the lobby on any phone in the party: open ${window.location.host}/display/ followed by that code here, or tap Cast to TV in the lobby.`
                  : 'Wait a few minutes, then reload this page. In the lobby on any phone, tap Cast to TV for this party’s audience link.'}
              </p>
            </div>
          </div>
        ) : (
          <LoadingScreen big label="Connecting the audience screen…" />
        )}
      </Stage>
    );
  return (
    <PartyUiContext.Provider value={ui}>
      <DisplayBody />
    </PartyUiContext.Provider>
  );
}

/**
 * Every stage shares one TV frame: a header strip (game art and name, round/phase with a caption of what's
 * happening, timer, party code + QR), the game's own Display in the main area, and a live scoreboard down
 * the right. The page never scrolls; the main area scales the game's screen down if it would overflow.
 */
function DisplayBody() {
  const { room, game } = usePartyState() as ReturnType<typeof usePartyState> & { room: NonNullable<ReturnType<typeof usePartyState>['room']> };
  const url = inviteUrl(room.code);
  const players = room.members.filter((m) => !m.spectator);
  const gameId = (game?.gameId ?? room.selectedGame) as GameId;
  const inGame = room.stage === 'IN_GAME' && !!game;
  const final = room.stage === 'FINAL_RESULTS';
  // Background music: this screen only (never phones), while the host's "TV music" setting is on.
  const musicAllowed = room.settings.tvMusic !== false;
  useTvMusicDriver(room.stage, musicAllowed, gameId);
  return (
    <Stage game={gameId} className="text-[1.15rem] h-dvh overflow-hidden">
      <div className="flex-1 min-h-0 flex flex-col" style={tvTheme(gameId)} data-testid="tv-frame" data-stage={room.stage} data-phase={inGame && game ? `${game.phase.name}:${game.phase.round}` : room.stage}>
        {inGame && game ? (
          <>
            <TvHeader
              gameId={gameId} kicker={phaseKicker(game.phase)} caption={phaseCaption(gameId, game.phase.name)}
              endsAt={game.phase.endsAt} startedAt={game.phase.startedAt} quietTimer={game.phase.name !== 'ACTIVE_ROUND' && game.phase.name !== 'VOTING'}
              showJoin joinUrl={url} code={room.code}
            />
            <div className="flex-1 min-h-0 flex">
              <FitBox id={screenKey(game.phase)}>
                <PhaseSwap id={screenKey(game.phase)}>
                  {(() => {
                    const { Display } = GAME_UI[game.gameId];
                    return (
                      <Suspense fallback={<GameLoading gameId={game.gameId} big />}>
                        <Display game={game} view={game.view} />
                      </Suspense>
                    );
                  })()}
                </PhaseSwap>
              </FitBox>
              <TvScoreboard scores={game.scores} />
            </div>
          </>
        ) : final ? (
          <>
            <TvHeader gameId={(room.lastResults?.gameId ?? gameId) as GameId} kicker="Final results" caption={phaseCaption(gameId, 'FINAL_RESULTS')} showJoin joinUrl={url} code={room.code} />
            <div className="flex-1 min-h-0 flex">
              <FitBox id="final">
                <main className="flex-1 px-10 py-6 max-w-5xl w-full mx-auto grid gap-6 content-start"><GrudgeMatch readOnly big /><ResultsBody big /></main>
              </FitBox>
              {room.lastResults && <TvScoreboard title="Final scores" scores={room.lastResults.ranking.map((r): ScoreLine => ({ playerId: r.playerId, total: r.score, lastRound: 0, rank: r.rank, streak: 0 }))} />}
            </div>
          </>
        ) : (
          <>
            <TvLobbyHeader gameId={gameId} />
            <span className="sr-only" data-testid="display-code">{room.code}</span>
            <TvLobby url={url} code={room.code} players={players} gameId={gameId} />
          </>
        )}
      </div>
      <FeedTicker big />
      <TvMusicProbe allowed={musicAllowed} />
    </Stage>
  );
}

type LobbyPlayer = { id: string; avatar: Parameters<typeof Avatar>[0]['id']; color: string; nickname: string; bot?: unknown };

/**
 * TV lobby: how to join (QR, link, big party code) and who's in on one side, the selected game's art,
 * pitch and how-to-play steps on the other. It is laid out on a fixed canvas (16:9 on wide screens,
 * portrait on tall ones) and scaled to fit the space under the header, so it always fits the whole screen,
 * at any size, with up to 20 players, without scrolling or distortion.
 */
function TvLobby({ url, code, players, gameId }: { url: string; code: string; players: LobbyPlayer[]; gameId: GameId }) {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight * 0.85 });
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => e && setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const wide = size.w >= size.h * 1.1;
  const W = wide ? 1600 : 900;
  const H = wide ? 900 : 1500;
  const scale = Math.max(0.1, Math.min(size.w / W, size.h / H));
  const n = players.length;
  // Avatars shrink as the party grows: wide fits 6 a row in the join column, tall 5 a row.
  const perRow = wide ? 6 : 5;
  const rows = Math.max(1, Math.ceil(n / perRow));
  const avatar = wide ? (rows === 1 ? 92 : rows === 2 ? 80 : rows === 3 ? 62 : 50) : rows <= 2 ? 104 : rows === 3 ? 86 : 70;
  const host = `${new URL(url).host}/join`;
  const g = gameInfo(gameId);
  const art = ART[gameId];
  const join = (
    <div className={`flex flex-col ${wide ? 'gap-6' : 'gap-6 items-center text-center'}`} style={{ width: wide ? 700 : W - 80 }}>
      <div className={`flex items-center ${wide ? 'gap-8' : 'flex-col gap-5'}`}>
        <QR value={url} size={wide ? 230 : 240} />
        <div className="min-w-0">
          <p className="text-mute text-[28px]">Grab your phone and go to</p>
          <p className="font-display font-black text-[40px] leading-tight break-all">{host}</p>
          <p className="text-mute text-[28px] mt-3">or scan the code</p>
        </div>
      </div>
      <div>
        <p className="text-mute text-[28px]">Party code</p>
        <p className={`font-display font-black leading-none tracking-[0.1em] whitespace-nowrap tabular-nums ${code.length <= 5 ? 'text-[164px]' : 'text-[132px]'}`} style={{ textShadow: '0 8px 0 var(--tv)' }}>{code}</p>
      </div>
      <div>
        <p className="font-display font-black text-[30px] mb-3">{n === 0 ? 'Waiting for players…' : `${n} ${n === 1 ? 'player' : 'players'} in`}</p>
        <div className={`flex flex-wrap gap-x-4 gap-y-3 ${wide ? '' : 'justify-center'}`}>
          {players.map((m) => (
            <div key={m.id} className="flex flex-col items-center gap-1 animate-pop text-center" style={{ width: avatar + 24 }}>
              <Avatar id={m.avatar} size={avatar} color={m.color} />
              <span className="font-display font-black leading-tight line-clamp-2 break-words max-w-full" style={{ fontSize: Math.round(avatar * 0.24) }}>
                {m.nickname}{m.bot ? <span className="text-mute" style={{ fontSize: Math.round(avatar * 0.17) }}> (AI)</span> : null}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  // A portrait screen has no room for the steps under the join column: there the card shows the pitch only.
  const steps = wide ? g.instructions.slice(0, 4) : [];
  const card = (
    <section className="relative overflow-hidden rounded-[36px] border-2 flex flex-col" style={{ width: wide ? 780 : W - 80, maxHeight: wide ? 820 : 700, borderColor: 'var(--tv)', background: 'var(--tv-bg)', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)' }} data-testid="tv-lobby-game">
      <div className="relative shrink-0" style={{ height: wide ? 300 : 210 }}>
        {art && <img src={art.cover} alt="" aria-hidden draggable={false} className="absolute inset-0 w-full h-full object-cover select-none" />}
        <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 35%, var(--tv-bg))' }} />
        <div className="absolute left-7 right-7 bottom-3 flex items-end gap-5">
          <GameIcon gameId={gameId} size={120} />
          <div className="min-w-0 pb-1">
            <p className="font-display font-black text-[30px] leading-none" style={{ color: 'var(--tv-2)' }}>Up next:</p>
            <p className="font-display font-black text-[64px] leading-[0.95]" style={{ color: 'var(--tv)', textShadow: '0 4px 0 rgba(0,0,0,0.6)' }}>{g.name}</p>
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 px-8 pb-6 pt-2 flex flex-col gap-4">
        <p className="text-[26px] font-bold leading-snug text-paper/90">{g.tagline}</p>
        {steps.length > 0 && <ol className="grid gap-3.5">
          {steps.map((st, i) => (
            <li key={st.title} className="flex gap-4 items-start">
              <span className="rounded-2xl p-2.5 shrink-0" style={{ background: 'var(--tv)', color: 'var(--tv-bg)' }}><NamedIcon name={st.icon} size={30} /></span>
              <div className="min-w-0">
                <p className="font-display font-black text-[26px] leading-tight">{i + 1}. {st.title}</p>
                <p className="text-mute text-[20px] leading-snug line-clamp-2">{st.body}</p>
              </div>
            </li>
          ))}
        </ol>}
      </div>
    </section>
  );
  return (
    <main ref={box} className="flex-1 min-h-0 relative overflow-hidden bg-cover bg-center" style={{ backgroundImage: 'linear-gradient(rgba(14,10,24,0.6), rgba(14,10,24,0.6)), url(/tv-lobby-bg.webp)' }} data-testid="tv-lobby">
      <div
        className={`absolute left-1/2 top-1/2 flex items-center ${wide ? 'flex-row justify-between px-10' : 'flex-col justify-evenly py-6'}`}
        style={{ width: W, height: H, transform: `translate(-50%, -50%) scale(${scale})`, transformOrigin: 'center' }}
      >
        {join}
        {card}
      </div>
      <SiteFooter big className="absolute bottom-1 right-4 z-10" />
    </main>
  );
}
